import React, { useCallback, useEffect } from "react"
// import QRTop from "pages/QRTop"
import { useDispatch, useSelector } from "react-redux"
import Top from "pages/Top/Top"
import Upload from "pages/Upload"
import Result from "pages/Result"
import ConditionsBefore from "pages/Conditions/ConditionsBefore"
import Complete from "pages/Complete"
import Finished from "pages/Finished"
import PlansBefore from "pages/Plans/PlansBefore"
import Usage from "pages/Usage"
import Estimable from "pages/Estimable"
import Error from "pages/Error"
import Maintenance from "pages/Maintenance"
import { MediaQueryProvider } from "utils/MediaQuery"
import { BrowserRouter as Router, Routes, Route } from "react-router-dom"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import ScrollTop from "components/ScrollTop"
import { APP_URL } from "Const"
import "./App.scss"
import { startDateCheck } from "utils/CompensationStartDateCheck"
import Conditions from "pages/Conditions/Conditions"
import Plans from "pages/Plans/Plans"
import { getCompStartDate } from "utils/JsonManipulation"
import { AppState } from "store/store"
import cameraOffActions from "store/actions/CameraOffAction"
import TopCameraOff from "pages/Top/TopCameraOff"

// マテリアルUIの初期設定
const theme = createTheme({
  palette: {
    primary: {
      main: "#0046AE"
    },
    secondary: {
      main: "#fff"
    }
  },
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          color: "#000000DE"
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          paddingRight: 0,
          "@media (min-width: 600px)": {
            maxWidth: "100vw !important",
            paddingLeft: 0,
            paddingRight: 0
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: "#000000DE"
        },
        contained: {
          containedPrimary: {
            backgroundColor: "#FF8200",
            "&:hover": {
              backgroundColor: "#FF8200"
            }
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, .0)"
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "#7d7d7d"
          }
        }
      }
    },
    MuiTouchRipple: {
      styleOverrides: {
        root: {
          display: "none"
        }
      }
    },
    // モーダル（dialog）
    MuiDialog: {
      styleOverrides: {
        container: {
          overflowY: "auto"
        },
        paper: {
          width: "90vw",
          margin: "22px 5vw",
          borderRadius: "5px",
          padding: "0px",
          textAlign: "center",
          "@media (min-width: 640px)": {
            width: "80vw !important"
          }
        },
        paperScrollPaper: {
          maxHeight: "100%"
        },
        paperWidthSm: {
          "@media (min-width: 640px)": {
            maxWidth: "680px !important"
          }
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          width: "90vw",
          marginTop: "20px",
          padding: "0 !important",
          overflowY: "visible",
          "@media (min-width: 640px)": {
            width: "80vw !important",
            maxWidth: "680px !important"
          }
        }
      }
    }
  }
})
const App: React.FC = () => {
  if (process.env.NODE_ENV !== "development") {
    console.log = function noConsole() {}
  }

  // カメラ機能無し版の判定文字列クエリパラメータを取得し、共通分でアクセスできるようにステートで保持
  const dispatch = useDispatch()
  const setCameraOff = useCallback((value: boolean) => dispatch(cameraOffActions.updateCameraOff(value)), [dispatch])
  const cameraOff = useSelector((state: AppState) => state.cameraOff.cameraOff)
  useEffect(() => {
    if (window.location.pathname === APP_URL.TOP) {
      const queryParams = new URLSearchParams(window.location.search)
      setCameraOff(queryParams.get("camera") === "off")
    }
  }, [setCameraOff])

  // 現行版／カメラ機能無し版の描画判定
  function getTopComponent() {
    return cameraOff ? <TopCameraOff /> : <Top />
  }

  const compensationStartDate = getCompStartDate()

  if (startDateCheck(compensationStartDate)) {
    // 商品改定以降
    return (
      <MediaQueryProvider>
        <ThemeProvider theme={theme}>
          <Router>
            <ScrollTop>
              <Routes>
                <Route path={APP_URL.TOP} element={getTopComponent()} />
                <Route path="/qr" element={getTopComponent()} />
                <Route path={APP_URL.USAGE} element={<Usage />} />
                <Route path={APP_URL.UPLOAD} element={<Upload />} />
                <Route path={APP_URL.RESULT} element={<Result />} />
                <Route path={APP_URL.CONDITIONS} element={<Conditions />} />
                <Route path={APP_URL.COMPLETE} element={<Complete />} />
                <Route path={APP_URL.FINISHED} element={<Finished />} />
                <Route path={APP_URL.PLANS} element={<Plans />} />
                <Route path={APP_URL.Estimable} element={<Estimable />} />
                <Route path={APP_URL.ERROR} element={<Error />} />
                <Route path={APP_URL.MAINTENANCE} element={<Maintenance />} />
              </Routes>
            </ScrollTop>
          </Router>
        </ThemeProvider>
      </MediaQueryProvider>
    )
  }
  // 商品改定以前
  return (
    <MediaQueryProvider>
      <ThemeProvider theme={theme}>
        <Router>
          <ScrollTop>
            <Routes>
              <Route path={APP_URL.TOP} element={getTopComponent()} />
              <Route path="/qr" element={getTopComponent()} />
              <Route path={APP_URL.USAGE} element={<Usage />} />
              <Route path={APP_URL.UPLOAD} element={<Upload />} />
              <Route path={APP_URL.RESULT} element={<Result />} />
              <Route path={APP_URL.CONDITIONS} element={<ConditionsBefore />} />
              <Route path={APP_URL.COMPLETE} element={<Complete />} />
              <Route path={APP_URL.FINISHED} element={<Finished />} />
              <Route path={APP_URL.PLANS} element={<PlansBefore />} />
              <Route path={APP_URL.Estimable} element={<Estimable />} />
              <Route path={APP_URL.ERROR} element={<Error />} />
              <Route path={APP_URL.MAINTENANCE} element={<Maintenance />} />
            </Routes>
          </ScrollTop>
        </Router>
      </ThemeProvider>
    </MediaQueryProvider>
  )
}

export default App
